import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import {
    Phone,
    CheckCircle,
    ArrowRight,
    Lightning,
    WhatsappLogo,
    ShieldCheck,
} from "@phosphor-icons/react";

/**
 * QuickApply — Mobile+OTP ad-landing page.
 *
 * Route: /quick-apply/:campaign?
 *
 * Ultra-thin funnel for paid Facebook/Google/WhatsApp ads:
 *   Step 1: Name + Mobile → send OTP (POST /api/public/quick-apply/otp/request)
 *   Step 2: 6-digit OTP → create crm_lead + magic-link (POST verify)
 *   Step 3: Success → primary CTA "Chat on WhatsApp" + secondary "Add my CV"
 *
 * Design keeps the visual language of PublicApply (paper bg, tight tracking,
 * caps eyebrows) but strips every field that isn't strictly needed for CVR.
 */
export default function QuickApply() {
    const { campaign } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();

    // Query params can override / augment the route param.
    const cityHint = (params.get("city") || "").trim() || "";
    const roleInterest = (params.get("role") || "").trim() || "";
    const cmp = (campaign || params.get("cmp") || "default").trim();

    const [step, setStep] = useState("form"); // form | otp | done
    const [form, setForm] = useState({ name: "", phone: "" });
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [devCode, setDevCode] = useState(""); // shown only when SMS not configured
    const [result, setResult] = useState(null); // { magic_url, wa_deeplink, lead_id }
    const [resendIn, setResendIn] = useState(0);
    const timerRef = useRef(null);

    // Countdown for resend button.
    useEffect(() => {
        if (resendIn <= 0) return;
        timerRef.current = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(timerRef.current);
    }, [resendIn]);

    const handleRequestOtp = async (e) => {
        e?.preventDefault?.();
        setErr("");
        if (!form.name.trim() || form.name.trim().length < 2) {
            setErr("Please tell us your name.");
            return;
        }
        const digits = form.phone.replace(/\D/g, "");
        if (digits.length < 10) {
            setErr("Enter a valid 10-digit mobile number.");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/public/quick-apply/otp/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    phone: form.phone,
                    campaign: cmp,
                    city: cityHint || undefined,
                    role_interest: roleInterest || undefined,
                }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) {
                setErr(j.detail || j.message || "Could not send code. Try again.");
                return;
            }
            setStep("otp");
            setResendIn(30);
            setDevCode(j.dev_code || "");
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };

    const handleVerify = async (e) => {
        e?.preventDefault?.();
        setErr("");
        if (otp.replace(/\D/g, "").length !== 6) {
            setErr("Enter the 6-digit code we sent.");
            return;
        }
        setBusy(true);
        try {
            const r = await fetch(`${API}/public/quick-apply/otp/verify`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    phone: form.phone,
                    code: otp.trim(),
                    campaign: cmp,
                    city: cityHint || undefined,
                    role_interest: roleInterest || undefined,
                }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) {
                setErr(j.detail || j.message || "Invalid code.");
                return;
            }
            setResult(j);
            setStep("done");
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async () => {
        if (resendIn > 0) return;
        setOtp("");
        await handleRequestOtp();
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="quick-apply-page">
            {/* Top bar */}
            <div className="border-b border-[#E5E5E5] bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Brand size="h-7" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] inline-flex items-center gap-1">
                        <ShieldCheck size={12} weight="bold" /> Verified apply
                    </span>
                </div>
            </div>

            <div className="max-w-md mx-auto px-4 sm:px-6 py-10 sm:py-16">
                {step === "form" && (
                    <form
                        onSubmit={handleRequestOtp}
                        data-testid="quick-apply-step-form"
                        noValidate
                    >
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cobalt mb-3 inline-flex items-center gap-1">
                            <Lightning size={12} weight="fill" /> 30-second apply
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-ink mb-3">
                            Get shortlisted{" "}
                            <span className="text-cobalt">this week.</span>
                        </h1>
                        <p className="text-sm text-[#525252] mb-6">
                            Add your name and mobile — we&apos;ll WhatsApp you jobs
                            that match your profile. No CV needed to start.
                            {roleInterest ? (
                                <>
                                    {" "}
                                    <span className="font-bold text-ink">
                                        {roleInterest} roles
                                    </span>{" "}
                                    are hiring near you now.
                                </>
                            ) : null}
                        </p>

                        <label className="block mb-4">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                                Full name
                            </span>
                            <input
                                data-testid="quick-apply-name"
                                autoFocus
                                type="text"
                                autoComplete="name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="e.g. Priya Sharma"
                                className="mt-1 w-full border-2 border-ink bg-white px-4 py-3 text-base font-semibold focus:outline-none focus:ring-4 focus:ring-cobalt/20"
                                maxLength={80}
                            />
                        </label>

                        <label className="block mb-6">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                                Mobile number
                            </span>
                            <div className="mt-1 flex items-center border-2 border-ink bg-white">
                                <span className="px-3 text-base font-black text-[#525252] border-r-2 border-ink">
                                    +91
                                </span>
                                <input
                                    data-testid="quick-apply-phone"
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel-national"
                                    value={form.phone}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                                        })
                                    }
                                    placeholder="10-digit mobile"
                                    className="flex-1 px-4 py-3 text-base font-semibold focus:outline-none"
                                    maxLength={10}
                                />
                            </div>
                            <p className="mt-1 text-[11px] text-[#A3A3A3]">
                                We&apos;ll send a 6-digit code to verify. No spam, ever.
                            </p>
                        </label>

                        {err ? (
                            <p
                                data-testid="quick-apply-error"
                                className="text-sm font-bold text-[#DC2626] bg-[#FEF2F2] border border-[#FCA5A5] px-3 py-2 mb-4"
                            >
                                {err}
                            </p>
                        ) : null}

                        <button
                            data-testid="quick-apply-send-otp"
                            type="submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-paper px-6 py-4 text-base font-black uppercase tracking-wider hover:bg-cobalt transition-colors disabled:opacity-60"
                        >
                            {busy ? "Sending…" : (
                                <>
                                    Send code <ArrowRight size={18} weight="bold" />
                                </>
                            )}
                        </button>

                        <p className="mt-4 text-[11px] text-[#737373] leading-relaxed">
                            By continuing you agree to receive job matches via
                            WhatsApp & SMS. Standard rates may apply. You can
                            opt out any time.
                        </p>
                    </form>
                )}

                {step === "otp" && (
                    <form
                        onSubmit={handleVerify}
                        data-testid="quick-apply-step-otp"
                        noValidate
                    >
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cobalt mb-3 inline-flex items-center gap-1">
                            <Phone size={12} weight="fill" /> Verify your number
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-3">
                            Enter the code
                        </h1>
                        <p className="text-sm text-[#525252] mb-6">
                            We sent a 6-digit code to{" "}
                            <span className="font-bold text-ink">
                                +91 {form.phone.replace(/(\d{5})(\d{5})/, "$1 $2")}
                            </span>
                            . It expires in 10 minutes.
                        </p>

                        {devCode ? (
                            <p className="mb-4 text-[11px] font-black uppercase tracking-wider text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] px-3 py-2">
                                Dev mode — SMS provider not configured. Your code: {" "}
                                <span className="font-mono text-sm text-ink">{devCode}</span>
                            </p>
                        ) : null}

                        <label className="block mb-6">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                                6-digit code
                            </span>
                            <input
                                data-testid="quick-apply-otp-input"
                                autoFocus
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={otp}
                                onChange={(e) =>
                                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                                }
                                placeholder="••••••"
                                className="mt-1 w-full border-2 border-ink bg-white px-4 py-4 text-center text-2xl font-black tracking-[0.4em] focus:outline-none focus:ring-4 focus:ring-cobalt/20"
                                maxLength={6}
                            />
                        </label>

                        {err ? (
                            <p
                                data-testid="quick-apply-error"
                                className="text-sm font-bold text-[#DC2626] bg-[#FEF2F2] border border-[#FCA5A5] px-3 py-2 mb-4"
                            >
                                {err}
                            </p>
                        ) : null}

                        <button
                            data-testid="quick-apply-verify"
                            type="submit"
                            disabled={busy || otp.length !== 6}
                            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-paper px-6 py-4 text-base font-black uppercase tracking-wider hover:bg-cobalt transition-colors disabled:opacity-40"
                        >
                            {busy ? "Verifying…" : (
                                <>
                                    Verify & continue{" "}
                                    <ArrowRight size={18} weight="bold" />
                                </>
                            )}
                        </button>

                        <div className="mt-4 flex items-center justify-between text-xs">
                            <button
                                data-testid="quick-apply-back"
                                type="button"
                                onClick={() => {
                                    setErr("");
                                    setOtp("");
                                    setStep("form");
                                }}
                                className="font-bold text-[#525252] hover:text-ink underline underline-offset-2"
                            >
                                Change number
                            </button>
                            <button
                                data-testid="quick-apply-resend"
                                type="button"
                                onClick={handleResend}
                                disabled={resendIn > 0}
                                className="font-bold text-cobalt hover:text-ink disabled:text-[#A3A3A3] underline underline-offset-2"
                            >
                                {resendIn > 0
                                    ? `Resend in ${resendIn}s`
                                    : "Resend code"}
                            </button>
                        </div>
                    </form>
                )}

                {step === "done" && result && (
                    <div data-testid="quick-apply-step-done" className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-5">
                            <CheckCircle
                                size={40}
                                weight="fill"
                                className="text-[#059669]"
                            />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#059669] mb-2">
                            You&apos;re verified
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-3">
                            Welcome{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                        </h1>
                        <p className="text-sm text-[#525252] mb-8">
                            Our recruiters will WhatsApp you within 24 hours with
                            roles that fit your profile. Tap below to fast-track
                            the conversation.
                        </p>

                        <div className="space-y-3">
                            {result.wa_deeplink ? (
                                <a
                                    data-testid="quick-apply-wa-cta"
                                    href={result.wa_deeplink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 text-base font-black uppercase tracking-wider hover:brightness-95 transition"
                                >
                                    <WhatsappLogo size={20} weight="fill" />
                                    Chat on WhatsApp
                                </a>
                            ) : null}

                            {result.magic_url ? (
                                <a
                                    data-testid="quick-apply-portal-cta"
                                    href={result.magic_url}
                                    className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-ink text-ink px-6 py-4 text-base font-black uppercase tracking-wider hover:bg-ink hover:text-paper transition"
                                >
                                    Add my CV — takes 60 sec
                                    <ArrowRight size={18} weight="bold" />
                                </a>
                            ) : null}
                        </div>

                        <p className="mt-6 text-[11px] text-[#A3A3A3]">
                            Lead #{(result.lead_id || "").slice(0, 8).toUpperCase()}
                            {cmp && cmp !== "default" ? ` · ${cmp}` : ""}
                        </p>
                    </div>
                )}

                {/* Trust bar */}
                <div className="mt-10 pt-6 border-t border-[#E5E5E5] grid grid-cols-3 gap-3 text-center">
                    {[
                        ["12,400+", "hires"],
                        ["48 hrs", "avg reply"],
                        ["100%", "free"],
                    ].map(([big, small]) => (
                        <div key={small}>
                            <p className="font-heading text-lg font-black tracking-tighter text-ink">
                                {big}
                            </p>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                                {small}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
